import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayerCard } from "@/context/PlayerCardContext";
import ChampBadge from "@/components/ChampBadge";
import FilmPsiCompare from "@/components/FilmPsiCompare";
import { PageHeader, RadialGauge, Pill, AiCard, RatedBadge } from "@/components/perf";
import Avatar from "@/components/Avatar";
import FilmScoreHistory from "@/components/FilmScoreHistory";
import FollowStats from "@/components/FollowStats";
import AffiliateCard from "@/components/AffiliateCard";
import AdBanner from "@/components/AdBanner";
import CardWarsEntry from "@/components/CardWarsEntry";
import SportsCardStudio from "@/components/SportsCardStudio";
import { BadgeChip } from "@/components/BadgeChip";
import { BrainHubCard } from "@/components/BrainHubCard";
import { toast } from "sonner";
import { Check, Crown, Loader2, Camera, MapPin, Pencil, Receipt, Package, Zap, ShoppingBag, X, Trophy, ChevronDown, Swords, Phone, KeyRound, Users, Flag, FlaskConical } from "lucide-react";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { TierIcon } from "@/components/tierIcons";
import MatrixRain from "@/components/MatrixRain";
import TournamentHistoryModal from "@/components/TournamentHistoryModal";
import Collapse from "@/components/Collapse";
import GateProgress from "@/components/GateProgress";
import DuprLinkCard from "@/components/DuprLinkCard";

export default function Profile() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { openPlayerCard } = usePlayerCard();
  const [pm, setPm] = useState(null);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(true);
  const [insightBusy, setInsightBusy] = useState(false);
  const [insightFee, setInsightFee] = useState(5);

  const genInsight = async () => {
    setInsightBusy(true);
    try {
      const { data } = await api.get("/profile/insights", { params: insight ? { force: true } : { generate: true } });
      setInsight(data.insight);
      if (data.coins_charged > 0) toast.success(`Fresh insight generated — ${data.coins_charged} BLC`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setInsightBusy(false); }
  };
  const [tiers, setTiers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [params, setParams] = useSearchParams();
  const [editingLoc, setEditingLoc] = useState(false);
  const [editingYear, setEditingYear] = useState(false);
  const [birthYear, setBirthYear] = useState("");
  const [editingExt, setEditingExt] = useState(false);
  const [vair, setVair] = useState("");
  const [dupr, setDupr] = useState("");
  const [duprCfg, setDuprCfg] = useState({ connect_enabled: true, manual_dupr_allowed: false });
  const [rank, setRank] = useState(null);
  const [h2h, setH2h] = useState([]);
  const [challenged, setChallenged] = useState({});

  const sendChallenge = async (r) => {
    setChallenged((c) => ({ ...c, [r.name]: "sending" }));
    try {
      await api.post("/rec-games/challenge", { opponent_name: r.name });
      setChallenged((c) => ({ ...c, [r.name]: "sent" }));
      toast.success(`⚔️ Challenge sent to ${r.name}!`);
    } catch (e) {
      setChallenged((c) => ({ ...c, [r.name]: null }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const [showLb, setShowLb] = useState(false);
  const [lb, setLb] = useState(null);
  const [lbGender, setLbGender] = useState("");
  const [tdCert, setTdCert] = useState(null);
  useEffect(() => { api.get("/training/certificate/mine").then(({ data }) => data.id && setTdCert(data)).catch(() => {}); }, []);
  const [showHistory, setShowHistory] = useState(false);
  const [showShopTeaser, setShowShopTeaser] = useState(() => localStorage.getItem("pb_hide_shop_teaser") !== "1");
  const dismissTeaser = () => { localStorage.setItem("pb_hide_shop_teaser", "1"); setShowShopTeaser(false); };
  const [loc, setLoc] = useState("");
  const [phone, setPhone] = useState("");
  const [editingPhone, setEditingPhone] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [nameVal, setNameVal] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [showBasicInfo, setShowBasicInfo] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [zip, setZip] = useState("");
  const [editingZip, setEditingZip] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setNameVal(user?.name || ""); }, [user?.name]);
  useEffect(() => { setEmailVal(user?.email || ""); }, [user?.email]);
  useEffect(() => { setLoc(user?.location || ""); }, [user?.location]);
  useEffect(() => { setZip(user?.zip_code || ""); }, [user?.zip_code]);
  useEffect(() => { setPhone(user?.phone || ""); }, [user?.phone]);
  useEffect(() => { setBirthYear(user?.birth_year || ""); }, [user?.birth_year]);
  useEffect(() => { setVair(user?.vair_score ?? ""); setDupr(user?.dupr_score ?? ""); }, [user?.vair_score, user?.dupr_score]);
  useEffect(() => { api.get("/dupr/public-config").then(({ data }) => setDuprCfg(data)).catch(() => {}); }, []);

  useEffect(() => {
    (async () => {
      const [p, t, o, r] = await Promise.all([api.get("/profile"), api.get("/membership/tiers"), api.get("/orders/mine"), api.get("/psi/rank")]);
      setPm(p.data.player_model); setTiers(t.data); setOrders(o.data); setRank(r.data);
      try { const { data } = await api.get("/rec-games/h2h"); setH2h(data); } catch { /* ignore */ }
    })();
    (async () => {
      try { const { data } = await api.get("/profile/insights"); setInsight(data.insight); setInsightFee(data.refresh_fee_blc ?? 5); }
      finally { setInsightLoading(false); }
    })();
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const s = Math.min(img.width, img.height);
        canvas.width = 256; canvas.height = 256;
        canvas.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, 256, 256);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        try {
          await api.put("/profile", { avatar: dataUrl });
          toast.success("Profile photo updated");
          await refresh();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const saveName = async () => {
    const n = (nameVal || "").trim();
    if (n.length < 2 || n.length > 60) return toast.error("Name must be 2-60 characters");
    try {
      await api.put("/profile", { name: n });
      toast.success("Name updated");
      setEditingName(false);
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveEmail = async () => {
    const em = (emailVal || "").trim();
    try {
      await api.put("/profile", { email: em });
      toast.success("Login email updated — use the new email next time you log in");
      setEditingEmail(false);
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveLocation = async () => {
    try {
      await api.put("/profile", { location: loc });
      toast.success("Location updated");
      setEditingLoc(false);
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveZip = async () => {
    try {
      await api.put("/profile", { zip_code: zip });
      toast.success("Home zip saved — you'll get local tournament & purse alerts");
      setEditingZip(false);
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const savePhone = async () => {
    try {
      await api.put("/profile", { phone });
      toast.success("Phone number saved — it'll auto-fill on bookings and forms");
      setEditingPhone(false);
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openLeaderboard = async () => {
    setShowLb(true);
    loadLeaderboard(lbGender || "");
  };

  const loadLeaderboard = async (g) => {
    setLbGender(g);
    setLb(null);
    try { const { data } = await api.get(`/psi/leaderboard${g ? `?gender=${g}` : ""}`); setLb(data); }
    catch { toast.error("Couldn't load the leaderboard"); }
  };

  const saveBirthYear = async () => {
    const yr = Number(birthYear);
    if (!yr || yr < 1900 || yr > new Date().getFullYear() - 4) return toast.error("Enter a valid birth year");
    try {
      await api.put("/profile", { birth_year: yr });
      toast.success("Birth year saved — age now factors into your match PSI");
      setEditingYear(false);
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveExternalRatings = async () => {
    const v = vair === "" ? 0 : Number(vair);
    const d = dupr === "" ? 0 : Number(dupr);
    if (vair !== "" && (isNaN(v) || v < 2 || v > 8)) return toast.error("VAIR must be between 2.0 and 8.0");
    if (duprCfg.manual_dupr_allowed && dupr !== "" && (isNaN(d) || d < 2 || d > 8)) return toast.error("DUPR must be between 2.0 and 8.0");
    try {
      await api.put("/profile", duprCfg.manual_dupr_allowed ? { dupr_score: d, vair_score: v } : { vair_score: v });
      toast.success(duprCfg.manual_dupr_allowed ? "External ratings saved — they now factor into your PSI at 5%" : "VAIR saved — it now factors into your PSI at 5%");
      setEditingExt(false);
      await refresh();
      const { data } = await api.get("/profile");
      setPm(data.player_model);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useAutoRefresh(async () => {
    const [p, o, r] = await Promise.all([api.get("/profile"), api.get("/orders/mine"), api.get("/psi/rank")]);
    setPm(p.data.player_model); setOrders(o.data); setRank(r.data);
  });

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.error("Payment status check timed out."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setVerifying(false); toast.success("VIP membership activated! 👑"); await refresh(); return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch { setVerifying(false); toast.error("Could not verify payment."); }
  }, [refresh]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true); pollStatus(sid);
      params.delete("session_id"); setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  const subscribe = async (t) => {
    setBusy(true);
    try {
      if (!t.price) {
        await api.post("/membership/subscribe", { tier: t.id });
        toast.success(`Switched to ${t.name} membership`);
        await refresh();
      } else {
        const { data } = await api.post("/payments/checkout", { package_id: t.id, origin_url: window.location.origin });
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (user && user.is_td && user.role !== "admin") return <Navigate to="/tournaments" replace />;

  return (
    <div data-testid="profile-page" className="relative">
      <MatrixRain />
      <div className="relative z-[1]">
      <PageHeader kicker="Player Model · Live Performance Identity"
        title={<>Player Performance Profile{" "}
          <button data-testid="players-lab-btn" onClick={() => navigate("/lab")} title="Enter the Player's Lab"
            className="inline-flex align-baseline ml-2 text-cyan-300 hover:text-cyan-400 transition-colors active:scale-95 drop-shadow-[0_0_10px_rgba(34,211,238,0.65)] animate-[bounce_3s_ease-in-out_infinite]">
            <FlaskConical className="w-8 h-8 sm:w-10 sm:h-10" />
          </button>
        </>}
        subtitle="Your performance identity, computed continuously from every rep and match. This is who you are as a player, in data." />

      {showShopTeaser && user?.role !== "admin" && (
        <div data-testid="pro-shop-teaser" className="relative bg-pb-surface border border-pb-red/40 p-5 mb-8 flex flex-wrap items-center gap-4 animate-fade-up">
          <div className="w-12 h-12 bg-pb-red/10 border border-pb-red/40 flex items-center justify-center shrink-0">
            <ShoppingBag className="w-6 h-6 text-pb-red" />
          </div>
          <div className="flex-1 min-w-[220px] pr-8">
            <div className="font-display font-black uppercase text-lg leading-none">
              Coming Soon — <span className="text-pb-red">The Pro Shop</span>
            </div>
            <p className="text-sm text-zinc-400 mt-1.5">Performance-curated paddles, gear and training tools are on the way. Bullpen members get first access.</p>
          </div>
          <button data-testid="dismiss-shop-teaser" onClick={dismissTeaser} title="Dismiss"
            className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-pb-red border border-transparent hover:border-pb-border transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <FollowStats />

      <AdBanner zone="profile" />

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Identity card */}
        <div className="bg-pb-surface border border-pb-border p-6">
          <CardWarsEntry testId="cardwars-entry-profile" />
          <button data-testid="road-to-nationals-btn" onClick={() => navigate("/road-to-nationals")}
            className="w-full mb-4 flex items-center justify-center gap-2 border border-[#BEF264]/60 bg-[#BEF264]/10 text-[#BEF264] px-4 py-3 text-[11px] font-black uppercase tracking-[0.2em] hover:bg-[#BEF264] hover:text-black transition-all active:scale-[.98]">
            <Flag className="w-4 h-4" /> Road to Nationals · PBR Ranking
          </button>
          {user?.featured_card && (
            <div className="relative mb-6" data-testid="profile-featured-card">
              <img src={user.featured_card} alt="My sports card" className="w-full max-w-[260px] mx-auto border border-amber-400/40 block" />
              <div className="text-center text-[9px] uppercase tracking-[0.25em] text-amber-400 mt-2 font-bold">Featured Sports Card</div>
            </div>
          )}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative shrink-0">
              <Avatar src={user?.avatar} name={user?.name} size={80} testId="profile-avatar" />
              <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()}
                title="Upload profile photo"
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-pb-red text-black flex items-center justify-center hover:bg-[#BEF264] active:scale-95 transition-all border-2 border-pb-surface">
                <Camera className="w-4 h-4" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="avatar-file-input" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2" data-testid="profile-name-row">
                {editingName ? (
                  <span className="flex items-center gap-2">
                    <input data-testid="name-input" value={nameVal} onChange={(e) => setNameVal(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveName()} autoFocus
                      className="bg-pb-elevated border border-pb-border px-2 py-1 text-lg font-display font-black uppercase text-white w-48 focus:outline-none focus:border-pb-red" />
                    <button data-testid="name-save-btn" onClick={saveName} className="text-pb-red font-bold uppercase text-[10px] tracking-widest hover:text-[#BEF264]">Save</button>
                  </span>
                ) : (
                  <>
                    <div className="font-display font-black text-2xl uppercase leading-none" data-testid="profile-name">{user?.name}</div>
                    <button data-testid="name-edit-btn" onClick={() => setEditingName(true)} title="Edit your name"
                      className="text-zinc-600 hover:text-pb-red shrink-0"><Pencil className="w-3 h-3" /></button>
                  </>
                )}
              </div>
              <button data-testid="basic-info-toggle" onClick={() => setShowBasicInfo((v) => !v)}
                title={showBasicInfo ? "Hide profile info" : "Edit your profile info"}
                className="flex items-center gap-1.5 mt-1.5 text-xs text-zinc-400 hover:text-pb-red transition-colors group">
                <span className="uppercase tracking-[0.15em] font-bold text-[10px]">Player Profile</span>
                <Pencil className="w-3 h-3 text-zinc-500 group-hover:text-pb-red transition-colors" />
                <ChevronDown className={`w-3 h-3 text-zinc-600 transition-transform duration-200 ${showBasicInfo ? "rotate-180" : ""}`} />
              </button>
              {showBasicInfo && (<div className="animate-fade-up" data-testid="basic-info-panel">
              <div className="flex items-center gap-1.5 mt-1 text-xs text-zinc-500" data-testid="profile-email-row">
                {editingEmail ? (
                  <span className="flex items-center gap-2">
                    <input data-testid="email-input" type="email" value={emailVal} onChange={(e) => setEmailVal(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveEmail()} autoFocus
                      className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-48 focus:outline-none focus:border-pb-red" />
                    <button data-testid="email-save-btn" onClick={saveEmail} className="text-pb-red font-bold uppercase text-[10px] tracking-widest hover:text-[#BEF264]">Save</button>
                  </span>
                ) : (
                  <>
                    <span className="truncate" data-testid="profile-email">{user?.email}</span>
                    <button data-testid="email-edit-btn" onClick={() => setEditingEmail(true)} title="Edit your login email"
                      className="text-zinc-600 hover:text-pb-red shrink-0"><Pencil className="w-3 h-3" /></button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-2 text-xs text-zinc-400" data-testid="profile-location-row">
                <MapPin className="w-3.5 h-3.5 text-pb-cyan shrink-0" />
                {editingLoc ? (
                  <span className="flex items-center gap-2">
                    <input data-testid="location-input" value={loc} onChange={(e) => setLoc(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveLocation()}
                      placeholder="City, State" autoFocus
                      className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-36 focus:outline-none focus:border-pb-red" />
                    <button data-testid="location-save-btn" onClick={saveLocation} className="text-pb-red font-bold uppercase text-[10px] tracking-widest hover:text-[#BEF264]">Save</button>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span data-testid="profile-location">{user?.location || "Add your city"}</span>
                    <button data-testid="location-edit-btn" onClick={() => setEditingLoc(true)} className="text-zinc-600 hover:text-pb-red"><Pencil className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-zinc-400" data-testid="profile-zip-row">
                <MapPin className="w-3.5 h-3.5 text-pb-cyan shrink-0" />
                {editingZip ? (
                  <span className="flex items-center gap-2">
                    <input data-testid="zip-input" value={zip} onChange={(e) => setZip(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveZip()}
                      placeholder="Home zip code" autoFocus maxLength={10}
                      className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-36 focus:outline-none focus:border-pb-red" />
                    <button data-testid="zip-save-btn" onClick={saveZip} className="text-pb-red font-bold uppercase text-[10px] tracking-widest hover:text-[#BEF264]">Save</button>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span data-testid="profile-zip">{user?.zip_code ? `Zip ${user.zip_code}` : "Add home zip — get local event alerts"}</span>
                    <button data-testid="zip-edit-btn" onClick={() => setEditingZip(true)} className="text-zinc-600 hover:text-pb-red"><Pencil className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-zinc-400" data-testid="profile-phone-row">
                <Phone className="w-3.5 h-3.5 text-pb-cyan shrink-0" />
                {editingPhone ? (
                  <span className="flex items-center gap-2">
                    <input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && savePhone()}
                      placeholder="941-555-0000" autoFocus
                      className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-36 focus:outline-none focus:border-pb-red" />
                    <button data-testid="phone-save-btn" onClick={savePhone} className="text-pb-red font-bold uppercase text-[10px] tracking-widest hover:text-[#BEF264]">Save</button>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span data-testid="profile-phone">{user?.phone || "Add phone (auto-fills bookings & forms)"}</span>
                    <button data-testid="phone-edit-btn" onClick={() => setEditingPhone(true)} className="text-zinc-600 hover:text-pb-red"><Pencil className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-zinc-400" data-testid="profile-password-row">
                <KeyRound className="w-3.5 h-3.5 text-pb-cyan shrink-0" />
                <button data-testid="change-password-btn" onClick={() => setShowPwModal(true)}
                  className="hover:text-pb-red transition-colors">Change password</button>
              </div>
              {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-zinc-400" data-testid="profile-birthyear-row">
                <Zap className="w-3.5 h-3.5 text-pb-cyan shrink-0" />
                {editingYear ? (
                  <span className="flex items-center gap-2">
                    <input data-testid="birthyear-input" type="number" value={birthYear} onChange={(e) => setBirthYear(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveBirthYear()}
                      placeholder="Birth year" autoFocus
                      className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-24 focus:outline-none focus:border-pb-red" />
                    <button data-testid="birthyear-save-btn" onClick={saveBirthYear} className="text-pb-red font-bold uppercase text-[10px] tracking-widest hover:text-[#BEF264]">Save</button>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span data-testid="profile-birthyear">{user?.birth_year ? `Born ${user.birth_year} · age factors into PSI` : "Add birth year (age factors into PSI)"}</span>
                    <button data-testid="birthyear-edit-btn" onClick={() => setEditingYear(true)} className="text-zinc-600 hover:text-pb-red"><Pencil className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-zinc-400" data-testid="profile-gender-row">
                <Users className="w-3.5 h-3.5 text-pb-cyan shrink-0" />
                <span className="flex items-center gap-2">
                  <span data-testid="profile-gender-label">{user?.gender ? `Gender: ${user.gender[0].toUpperCase()}${user.gender.slice(1)}` : "Gender (used for gendered events & Card Wars)"}</span>
                  <select data-testid="gender-select" value={user?.gender || ""}
                    onChange={async (e) => {
                      try {
                        await api.put("/profile", { gender: e.target.value });
                        toast.success(e.target.value ? "Gender saved" : "Gender cleared");
                        await refresh();
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    className="bg-pb-elevated border border-pb-border px-1.5 py-1 text-[11px] text-white focus:outline-none focus:border-pb-red cursor-pointer">
                    <option value="">—</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-zinc-400" data-testid="profile-external-ratings-row">
                <Trophy className="w-3.5 h-3.5 text-pb-cyan shrink-0" />
                {editingExt ? (
                  <span className="flex items-center gap-2 flex-wrap">
                    {duprCfg.manual_dupr_allowed && (
                      <input data-testid="dupr-input" type="number" step="0.001" min="2" max="8" value={dupr} onChange={(e) => setDupr(e.target.value)}
                        placeholder="DUPR" autoFocus
                        className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-20 focus:outline-none focus:border-pb-red" />
                    )}
                    <input data-testid="vair-input" type="number" step="0.001" min="2" max="8" value={vair} onChange={(e) => setVair(e.target.value)}
                      placeholder="VAIR" autoFocus={!duprCfg.manual_dupr_allowed}
                      className="bg-pb-elevated border border-pb-border px-2 py-1 text-xs text-white w-20 focus:outline-none focus:border-pb-red" />
                    <button data-testid="external-ratings-save-btn" onClick={saveExternalRatings} className="text-pb-red font-bold uppercase text-[10px] tracking-widest hover:text-[#BEF264]">Save</button>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span data-testid="profile-external-ratings">
                      {duprCfg.manual_dupr_allowed
                        ? (user?.dupr_score || user?.vair_score
                          ? [user?.dupr_score ? `DUPR ${Number(user.dupr_score).toFixed(2)}` : null, user?.vair_score ? `VAIR ${Number(user.vair_score).toFixed(2)}` : null].filter(Boolean).join(" · ")
                          : "Add DUPR / VAIR (optional)")
                        : (user?.vair_score ? `VAIR ${Number(user.vair_score).toFixed(2)}` : "Add VAIR (optional)")}
                      {!duprCfg.manual_dupr_allowed && user?.dupr_score ? <span className="text-zinc-600" data-testid="profile-legacy-seed"> · self-reported seed {Number(user.dupr_score).toFixed(2)}</span> : null}
                    </span>
                    <button data-testid="external-ratings-edit-btn" onClick={() => setEditingExt(true)} className="text-zinc-600 hover:text-pb-red"><Pencil className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <DuprLinkCard />
              </div>)}
              <div className="mt-2 flex gap-2 flex-wrap items-center">
                <Pill color="#A3E635">{pm?.division}</Pill>
                <Pill color="#4ADE80">{user?.membership} member</Pill>
                <span data-testid="ecosystem-rank-badge"><Pill color="#22D3EE">{rank ? `#${rank.rank} of ${rank.total} in the Bullpen` : "Rank —"}</Pill></span>
                {rank?.gender_rank && <span data-testid="gender-rank-badge"><Pill color={rank.gender_label === "men" ? "#F59E0B" : "#F472B6"}>{`#${rank.gender_rank} of ${rank.gender_total} ${rank.gender_label}`}</Pill></span>}
                <button data-testid="leaderboard-btn" onClick={openLeaderboard}
                  className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 hover:bg-pb-cyan hover:text-black active:scale-95 transition-all">
                  <Trophy className="w-3 h-3" /> Leaderboard
                </button>
                <button data-testid="profile-history-btn" onClick={() => setShowHistory(true)}
                  className="flex items-center gap-1.5 border border-yellow-500/60 text-yellow-400 font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 hover:bg-yellow-400 hover:text-black active:scale-95 transition-all">
                  <Trophy className="w-3 h-3" /> Tournament History
                </button>
                {tdCert && (
                  <a href={`/certificate/${tdCert.id}`} target="_blank" rel="noreferrer" data-testid="td-certificate-badge"
                    title={`${tdCert.cert_no} — Certified Tournament Director. Tap to view & share.`}
                    className="flex items-center gap-1.5 border-2 border-yellow-500 bg-yellow-500/10 text-yellow-300 font-black uppercase tracking-widest text-[10px] px-2.5 py-1 shadow-[0_0_12px_rgba(212,175,55,0.4)] hover:bg-yellow-500 hover:text-black active:scale-95 transition-all">
                    <Crown className="w-3 h-3" /> Certified TD
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-center">
            <RadialGauge value={pm?.skill_index ?? 0} label="Skill Index" color="#A3E635" size={170} />
          </div>
          <div className="text-center text-xs text-zinc-500 mt-2">Rating {typeof pm?.rating === "number" ? pm.rating.toFixed(2) : pm?.rating}</div>
          {pm?.next_level && (
            <div className="mt-3" data-testid="band-progress">
              <div className="flex justify-between text-[9px] uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
                <span>{pm.division}</span>
                <span className="text-pb-cyan font-bold">{pm.psi_to_next} PSI to {pm.next_level}</span>
              </div>
              <div className="h-1.5 bg-pb-elevated border border-pb-border overflow-hidden">
                <div className="h-full bg-pb-red transition-all duration-700" style={{ width: `${pm.band_progress}%` }} data-testid="band-progress-bar" />
              </div>
            </div>
          )}
        </div>

        {/* PBPA Court Mentality Training Hub */}
        <BrainHubCard />

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-4 content-start">
          {[
            { l: "Drill Efficiency", v: pm?.drill_efficiency, c: "#10B981" },
            { l: "Conversion Rate", v: pm?.competitive_conversion, c: "#4ADE80" },
            { l: "Pressure Stability", v: pm?.pressure_stability, c: "#A3E635" },
            { l: "Attendance Index", v: pm?.attendance_consistency, c: "#16A34A" },
          ].map((m, i) => (
            <div key={i} className="bg-pb-surface border border-pb-border p-4">
              <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{m.l}</div>
              <div className="font-display font-black text-2xl" style={{ color: m.c }}>{m.v ?? 0}<span className="text-xs text-zinc-600">/100</span></div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <SportsCardStudio />
      </div>

      {/* Ecosystem rank + PSI component breakdown (dropdown) */}
      <Collapse title="PSI Framework · Ecosystem Ranking" icon={Trophy} testid="psi-breakdown-toggle"
        badge={rank ? <Pill color="#22D3EE">Ranked #{rank.rank} · Top {Math.max(1, 100 - rank.percentile)}%</Pill> : null}>
      <div data-testid="psi-breakdown">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">PSI Framework · Ecosystem Ranking</div>
            <div className="font-display font-black text-2xl uppercase mt-1 flex items-center gap-3 flex-wrap">
              {rank ? <>Ranked <span className="text-pb-cyan">#{rank.rank}</span> of {rank.total} players{rank.gender_rank ? <span className="text-zinc-400"> · <span style={{ color: rank.gender_label === "men" ? "#F59E0B" : "#F472B6" }}>#{rank.gender_rank}</span> of {rank.gender_total} {rank.gender_label}</span> : null}</> : "Ecosystem Rank"}
              {pm?.badge && <BadgeChip badge={pm.badge} />}
            </div>
          </div>
          {rank && rank.total > 1 && (
            <div className="text-right">
              <div className="font-display font-black text-3xl text-pb-red" data-testid="psi-percentile">Top {Math.max(1, 100 - rank.percentile)}%</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">of the Bullpen</div>
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { key: "baseline", label: "Baseline Anchor", color: "#F59E0B", max: 198, sub: (pm?.psi_components?.self_reported_seed || pm?.psi_components?.vair) ? `anchored by ${[pm?.psi_components?.self_reported_seed ? `${duprCfg.manual_dupr_allowed ? "DUPR" : "self-reported seed"} ${Number(pm.psi_components.self_reported_seed).toFixed(2)}` : null, pm?.psi_components?.vair ? `VAIR ${Number(pm.psi_components.vair).toFixed(2)}` : null].filter(Boolean).join(" + ")}` : (duprCfg.manual_dupr_allowed ? "add your DUPR/VAIR above to raise your anchor" : "add your VAIR above to raise your anchor") },
            { key: "tournament", label: "Tournaments", color: "#22D3EE", max: 100, sub: "your strongest PSI driver", count: pm?.psi_components?.tournament_matches },
            { key: "league", label: "League Play", color: "#4ADE80", max: 100, sub: "BPL match results", count: pm?.psi_components?.league_matches },
            { key: "rec", label: "Rec Games", color: "#F472B6", max: 100, sub: pm?.psi_components?.rec_opt_in ? (pm?.psi_components?.rec_pending ? "log 4+ games in a day to activate scoring" : "creation play results") : "opt in on the LDA Training page", count: pm?.psi_components?.rec_matches },
            { key: "ai", label: "AI Gameplay", color: "#A78BFA", max: 100, sub: "game-film analysis average", count: pm?.psi_components?.ai_films, countLabel: "film" },
            { key: "drill_bonus", label: "Drill Bonus", color: "#A3E635", max: 2, sub: `${pm?.psi_components?.drills_completed ?? 0} of ${pm?.psi_components?.drills_total ?? 25} drills completed · pure bonus, never hurts` },
          ].map((c) => {
            const val = pm?.psi_components?.[c.key];
            const pct = val != null ? (val / c.max) * 100 : 0;
            return (
              <div key={c.key} className="border border-pb-border bg-pb-elevated/40 p-4" data-testid={`psi-component-${c.key}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{c.label}</span>
                </div>
                <div className="font-display font-black text-3xl" style={{ color: c.color }}>
                  {val != null ? val : "—"}<span className="text-xs text-zinc-600">/{c.max}</span>
                </div>
                <div className="h-1.5 bg-pb-base border border-pb-border mt-2 overflow-hidden">
                  <div className="h-full transition-all duration-700" style={{ width: `${Math.min(100, pct)}%`, background: c.color }} />
                </div>
                <div className="text-[10px] text-zinc-600 mt-2">
                  {val == null && c.key !== "drill_bonus" && c.key !== "baseline" ? `No ${c.label.toLowerCase()} data yet` : c.sub}{c.count ? ` · ${c.count} ${c.count === 1 ? (c.countLabel || "match") : (c.countLabel ? `${c.countLabel}s` : "matches")}` : ""}
                </div>
              </div>
            );
          })}
        </div>
        <FilmPsiCompare pm={pm} />
        <p className="text-[11px] text-zinc-600 mt-4">
          Your PSI lives on a 0–198 scale across nine divisions — Beginner (0–40) up through Pro (172.7–194.8) and the earned-only G.O.A.T. tier (194.9–198, requires a 99.9% win ratio). It starts from your Baseline Anchor (set by your self-reported seed, VAIR or assigned level — verified DUPR comes only from your connected DUPR account). From there it's earned the slow way: every tournament, league or rec result moves it a small amount — as little as ±0.01 for expected results, and never more than ±1.5 in a single tournament match (±1.0 in league). Upset wins over stronger teams move you the most; losses to stronger teams barely dent you. AI game film and rec games nudge it up to ±0.5, and drill completions add a pure bonus of up to +2 that never hurts. Earn your gem badge — Quartz to Black Diamond — as your PSI climbs. Full formula in the FAQ.
        </p>
      </div>
      </Collapse>

      <GateProgress />

      {/* AI Insights (dropdown) */}
      <Collapse title="AI Insights" icon={Zap} testid="insights-toggle"
        badge={<Pill color="#22D3EE">Emergent AI</Pill>}>
        <AiCard loading={insightLoading}>
          <div>
            {insight ? (
              <p className="text-sm text-zinc-200 leading-relaxed" data-testid="profile-insight-text">{insight}</p>
            ) : (
              <p className="text-sm text-zinc-500">Get a sharp AI read on your performance identity and the single biggest lever to climb a division.</p>
            )}
            <button data-testid="profile-generate-insight-btn" disabled={insightBusy} onClick={genInsight}
              className="mt-3 flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50">
              <Zap className="w-3.5 h-3.5" />
              {insightBusy ? "Generating…" : insight ? `Refresh Insight · ${insightFee} BLC` : "Generate AI Insight — Free"}
            </button>
          </div>
        </AiCard>
      </Collapse>

      {/* Film Score History (dropdown) */}
      <Collapse title="Film Score History" icon={Camera} testid="film-toggle"
        subtitle="Your AI-graded video analyses — every score feeds the drills window of your PSI.">
        <FilmScoreHistory psi={pm?.skill_index} />
      </Collapse>

      {/* Head-to-Head (dropdown) */}
      <Collapse title="Head-to-Head" icon={Swords} testid="h2h-toggle"
        badge={h2h.length > 0 ? <Pill color="#F472B6">{h2h.length} rival{h2h.length === 1 ? "" : "s"}</Pill> : null}
        subtitle="Your record against every opponent from your logged recreational games (LDA Training page).">
        {h2h.length === 0 ? (
          <div className="bg-pb-surface border border-pb-border p-6 text-sm text-zinc-500 max-w-3xl" data-testid="h2h-empty">
            No rivalries yet — log recreational games on the LDA Training page to start building your head-to-head records.
          </div>
        ) : (
          <div className="space-y-2.5 max-w-3xl" data-testid="h2h-list">
            {h2h.map((r, idx) => {
              const total = r.wins + r.losses;
              const pct = Math.round((r.wins / total) * 100);
              const slug = r.name.toLowerCase().replace(/\s+/g, "-");
              const state = challenged[r.name];
              return (
                <div key={r.name} data-testid={`h2h-row-${slug}`}
                  className="group relative flex items-center gap-3 sm:gap-4 border border-pb-border/70 bg-gradient-to-r from-pb-surface via-pb-surface/70 to-[#F472B6]/[0.06] px-4 py-3 transition-all duration-300 hover:border-[#F472B6]/50 hover:shadow-[0_0_22px_rgba(244,114,182,0.14)] hover:-translate-y-px animate-fade-up"
                  style={{ animationDelay: `${idx * 60}ms` }}>
                  <span className="pointer-events-none absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-[#F472B6] to-[#22D3EE] opacity-40 group-hover:opacity-100 transition-opacity" />
                  <span className="font-mono text-[10px] text-zinc-600 w-5 shrink-0">{String(idx + 1).padStart(2, "0")}</span>
                  <span className="w-9 h-9 rounded-full border border-[#F472B6]/50 bg-[#F472B6]/10 text-[#F472B6] font-display font-black flex items-center justify-center shrink-0 shadow-[0_0_12px_rgba(244,114,182,0.18)]">
                    {r.name.charAt(0).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase leading-tight truncate">{r.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-[8px] font-black uppercase tracking-[0.2em] px-1.5 py-px border ${r.member_id ? "border-pb-cyan/50 text-pb-cyan" : "border-pb-border text-zinc-600"}`}>
                        {r.member_id ? "Member" : "Guest"}
                      </span>
                      <span className="font-mono text-xs">
                        <span className="text-green-400">{r.wins}W</span>
                        <span className="text-zinc-600"> – </span>
                        <span className="text-red-400">{r.losses}L</span>
                      </span>
                    </div>
                  </div>
                  <div className="w-24 shrink-0 hidden sm:block">
                    <div className="flex justify-between text-[9px] font-mono mb-1">
                      <span className="text-zinc-600 uppercase tracking-widest">Win rate</span>
                      <span style={{ color: pct >= 50 ? "#4ADE80" : "#F87171" }}>{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-pb-elevated overflow-hidden rounded-full">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: "linear-gradient(90deg,#F472B6,#22D3EE)" }} />
                    </div>
                  </div>
                  {r.member_id && (
                    state === "sent" ? (
                      <span data-testid={`h2h-challenge-sent-${slug}`}
                        className="shrink-0 flex items-center gap-1.5 rounded-full border border-pb-cyan/60 text-pb-cyan px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.15em]">
                        <Check className="w-3.5 h-3.5" /> Sent
                      </span>
                    ) : (
                      <button data-testid={`h2h-challenge-${slug}`} onClick={() => sendChallenge(r)} disabled={state === "sending"}
                        className="shrink-0 flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-black bg-gradient-to-r from-[#F472B6] to-[#22D3EE] hover:shadow-[0_0_18px_rgba(34,211,238,0.45)] active:scale-95 transition-all disabled:opacity-60">
                        {state === "sending" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Swords className="w-3.5 h-3.5" />} Challenge
                      </button>
                    )
                  )}
                </div>
              );
            })}
            <p className="text-[10px] text-zinc-600 pt-1">
              <Swords className="w-3 h-3 inline mr-1 text-[#F472B6]" />
              Challenge a member rival and they'll get an instant notification to schedule a rematch.
            </p>
          </div>
        )}
      </Collapse>

      {/* Membership (dropdown) */}
      <Collapse title="Membership" icon={Crown} testid="membership-toggle"
        badge={<Pill color="#A3E635">{(user?.membership || "free").toUpperCase()}</Pill>}
        subtitle="Your account access tier. VIP unlocks league entry + film uploads. ACE unlocks everything — including Record LIVE AI analysis. Separate from drill session passes (see the Book tab).">
        <div className="grid md:grid-cols-3 gap-5">
        {tiers.map((t) => {
          const isCurrent = (user?.membership || "free") === t.id;
          const isPaid = t.price > 0;
          return (
            <div key={t.id} data-testid={`tier-${t.id}`} className={`bg-pb-surface border p-6 flex flex-col ${isCurrent ? "border-pb-red" : t.access === "ace" ? "border-pb-red/50" : isPaid ? "border-pb-cyan/40" : "border-pb-border"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-display font-black text-2xl uppercase flex items-center gap-2">
                  <TierIcon icon={t.icon} className="w-5 h-5 text-pb-red" />
                  {t.name}
                </span>
                {isCurrent && <Pill color="#A3E635">Current</Pill>}
              </div>
              <div className="font-display font-black text-3xl text-pb-cyan mb-4">${t.price}<span className="text-sm text-zinc-500">{t.recurring ? "/mo" : ""}</span></div>
              <ul className="space-y-2 mb-6 flex-1">
                {t.perks.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-pb-cyan mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <button data-testid={`subscribe-${t.id}`} onClick={() => subscribe(t)} disabled={busy || isCurrent}
                className={`w-full font-bold uppercase tracking-widest py-3 transition-all ${isCurrent ? "bg-pb-elevated text-zinc-500 cursor-default" : "bg-pb-red text-black hover:bg-[#BEF264] active:scale-95"}`}>
                {isCurrent ? "Active" : isPaid ? `Upgrade · $${t.price}/mo` : `Switch to ${t.name}`}
              </button>
            </div>
          );
        })}
      </div>
      </Collapse>

      {/* Order History (dropdown) */}
      <Collapse title="Order History" icon={Receipt} testid="orders-toggle"
        badge={<Pill color="#22D3EE">{orders.length}</Pill>}
        subtitle="Your past gear and session pass purchases, with tax and shipping receipts.">
      {orders.length === 0 ? (
        <div className="bg-pb-surface border border-pb-border p-6 text-sm text-zinc-500 max-w-3xl" data-testid="orders-empty">
          No orders yet. Grab gear from the Store or a session pass from Book — your receipts will show up here.
        </div>
      ) : (
        <div className="space-y-4 max-w-3xl" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="bg-pb-surface border border-pb-border p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <Package className="w-4 h-4 text-pb-cyan" />
                  {o.created_at ? new Date(o.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                  <span className="text-zinc-700">·</span>
                  <span className="font-mono">#{o.id.slice(-6).toUpperCase()}</span>
                </div>
                <Pill color="#4ADE80">{o.status}</Pill>
              </div>
              <div className="space-y-1.5 mb-3">
                {o.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-zinc-300">{it.qty}× {it.name}{it.type === "pass" ? <span className="text-pb-cyan text-[10px] uppercase tracking-widest ml-2">Pass</span> : null}</span>
                    <span className="font-mono text-zinc-400">${(it.price * it.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-pb-border/50 pt-2 space-y-1 text-xs text-zinc-500">
                {(o.tax > 0 || o.shipping > 0) && (
                  <>
                    <div className="flex justify-between"><span>Tax</span><span className="font-mono">${Number(o.tax).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{Number(o.shipping) === 0 ? "FREE" : `$${Number(o.shipping).toFixed(2)}`}</span></div>
                  </>
                )}
                <div className="flex justify-between items-center pt-1">
                  <span className="uppercase tracking-widest">Total</span>
                  <span className="font-display font-black text-lg text-pb-red">${Number(o.total).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </Collapse>

      <AffiliateCard />
      </div>

      {showHistory && user && <TournamentHistoryModal playerId={user.id} playerName={user.name} onClose={() => setShowHistory(false)} />}
      {showLb && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowLb(false)}>
          <div className="bg-pb-surface border border-pb-cyan w-full max-w-2xl my-8 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="leaderboard-modal">
            <div className="flex items-center justify-between p-5 border-b border-pb-border sticky top-0 bg-pb-surface z-10">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-pb-cyan" />
                <h3 className="font-display font-black text-lg uppercase">Bullpen Leaderboard</h3>
                {lb && <Pill color="#22D3EE">{lb.total} players</Pill>}
              </div>
              <button data-testid="leaderboard-close" onClick={() => setShowLb(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-1.5 px-5 pt-4" data-testid="leaderboard-gender-toggle">
              {[["", "All"], ["male", "Men"], ["female", "Women"]].map(([g, label]) => (
                <button key={label} data-testid={`lb-filter-${label.toLowerCase()}`} onClick={() => loadLeaderboard(g)}
                  className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest border transition-colors ${lbGender === g ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-400 hover:border-pb-cyan hover:text-pb-cyan"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="p-5">
              {!lb && <div className="text-sm text-zinc-500 py-8 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading rankings…</div>}
              {lb && lb.leaders.length === 0 && <div className="text-sm text-zinc-500 py-8 text-center" data-testid="leaderboard-empty">No ranked players here yet — set your gender on your profile to join this board.</div>}
              {lb && (
                <div className="space-y-1.5" data-testid="leaderboard-list">
                  {lb.leaders.map((r) => {
                    const isMe = r.user_id === user?.id;
                    const medal = r.rank === 1 ? "#FACC15" : r.rank === 2 ? "#D4D4D8" : r.rank === 3 ? "#D97706" : null;
                    return (
                      <div key={r.user_id} data-testid={`leaderboard-row-${r.rank}`}
                        onClick={() => openPlayerCard(r.user_id)} role="button"
                        className={`flex items-center gap-3 px-3 py-2 border cursor-pointer hover:border-pb-red/60 transition-colors ${isMe ? "border-pb-cyan bg-pb-cyan/10" : "border-pb-border/60 bg-pb-elevated/40"}`}>
                        <span className="font-display font-black text-lg w-8 text-center shrink-0" style={{ color: medal || "#71717A" }}>{r.rank}</span>
                        <Avatar src={r.avatar} name={r.name} size={30} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{r.name}<ChampBadge userId={r.user_id} /><RatedBadge dupr={r.dupr} vair={r.vair} />{isMe && <span className="text-pb-cyan text-[10px] uppercase tracking-widest ml-2">You</span>}</div>
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{r.division}{r.location ? ` · ${r.location}` : ""}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display font-black text-xl text-pb-red">{r.psi}</div>
                          <div className="text-[9px] uppercase tracking-widest text-zinc-600">PSI</div>
                        </div>
                      </div>
                    );
                  })}
                  {lb.you && lb.you.rank > lb.leaders.length && (
                    <div className="flex items-center gap-3 px-3 py-2 border border-pb-cyan bg-pb-cyan/10 mt-3" data-testid="leaderboard-you-row">
                      <span className="font-display font-black text-lg w-8 text-center text-pb-cyan shrink-0">{lb.you.rank}</span>
                      <Avatar src={lb.you.avatar} name={lb.you.name} size={30} />
                      <div className="flex-1 text-sm font-semibold truncate">{lb.you.name}<RatedBadge dupr={lb.you.dupr} vair={lb.you.vair} /> <span className="text-pb-cyan text-[10px] uppercase tracking-widest ml-1">You</span></div>
                      <div className="font-display font-black text-xl text-pb-red">{lb.you.psi}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {verifying && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center">
          <div className="bg-pb-surface border border-pb-cyan p-8 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-pb-cyan animate-spin" />
            <span className="font-display font-bold uppercase tracking-widest">Verifying Payment…</span>
          </div>
        </div>
      )}
    </div>
  );
}

